import React, { useState } from "react";
import { Modal, Rate, Select, Input, Divider, Tag } from "antd";
import { notify } from "../../../../services/toast.config";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import {
  submitInterviewFeedback,
  fetchInterviewDetails,
} from "../../../../core/data/redux/recruitment/thunks";
import type {
  Interview,
  CriteriaSchemaItem,
} from "../../../../core/data/redux/recruitment/types";
import { RECOMMENDATION_OPTIONS } from "../../constants/recruitment.constants";

const { TextArea } = Input;

interface FeedbackFormProps {
  show: boolean;
  onClose: () => void;
  interviewId: number;
  candidateName: string;
  onSubmitted: () => void;
}

// ─── Mini label component matching ScheduleInterviewModal's Label ─────────────
const Label: React.FC<{ text: string; required?: boolean }> = ({
  text,
  required,
}) => (
  <label
    style={{
      display: "block",
      fontSize: 11,
      fontWeight: 700,
      marginBottom: 6,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }}
  >
    {text}
    {required && (
      <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>
    )}
  </label>
);

// ─── Section header ────────────────────────────────────────────────────────────
const SectionHeader: React.FC<{
  icon: string;
  iconColor: string;
  iconBg: string;
  title: string;
}> = ({ icon, iconColor, iconBg, title }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 12,
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: iconBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <i className={`ti ${icon}`} style={{ fontSize: 14, color: iconColor }} />
    </div>
    <span
      style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.1px" }}
    >
      {title}
    </span>
  </div>
);

// ─── Rating Card ───────────────────────────────────────────────────────────────
const RatingCard: React.FC<{
  icon: string;
  iconColor: string;
  iconBg: string;
  label: string;
  required?: boolean;
  value: number;
  onChange: (v: number) => void;
  size?: "large" | "small";
}> = ({ icon, iconColor, iconBg, label, required, value, onChange, size = "small" }) => (
  <div
    style={{
      background: value > 0 ? "#fafaf9" : "#fff",
      border: `1.5px solid ${value > 0 ? "#fbbf24" : "#e2e8f0"}`,
      borderRadius: 12,
      padding: size === "large" ? "16px 20px" : "12px 14px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      transition: "border-color 0.2s, background 0.2s",
      cursor: "default",
    }}
  >
    <div
      style={{
        width: size === "large" ? 40 : 34,
        height: size === "large" ? 40 : 34,
        borderRadius: 10,
        background: iconBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <i
        className={`ti ${icon}`}
        style={{ fontSize: size === "large" ? 18 : 15, color: iconColor }}
      />
    </div>
    <div
      style={{
        fontSize: size === "large" ? 13 : 12,
        fontWeight: 600,
        color: "#334155",
        textAlign: "center",
        lineHeight: 1.3,
      }}
    >
      {label}
      {required && (
        <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>
      )}
    </div>
    <Rate
      value={value}
      onChange={onChange}
      style={{
        fontSize: size === "large" ? 22 : 16,
        color: "#f59e0b",
      }}
    />
    {value > 0 && (
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#92400e",
          background: "#fef3c7",
          padding: "2px 7px",
          borderRadius: 20,
          letterSpacing: "0.3px",
        }}
      >
        {["", "Poor", "Fair", "Good", "Great", "Excellent"][value]}
      </span>
    )}
  </div>
);

// ─── Recommendation color map ──────────────────────────────────────────────────
const REC_META: Record<
  string,
  { color: string; bg: string; border: string; icon: string }
> = {
  strong_yes: {
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#86efac",
    icon: "ti-circle-check",
  },
  yes: {
    color: "#0369a1",
    bg: "#f0f9ff",
    border: "#7dd3fc",
    icon: "ti-thumb-up",
  },
  maybe: {
    color: "#92400e",
    bg: "#fffbeb",
    border: "#fcd34d",
    icon: "ti-help-circle",
  },
  no: {
    color: "#b45309",
    bg: "#fff7ed",
    border: "#fdba74",
    icon: "ti-thumb-down",
  },
  strong_no: {
    color: "#991b1b",
    bg: "#fef2f2",
    border: "#fca5a5",
    icon: "ti-x",
  },
};

// ─── Main Component ────────────────────────────────────────────────────────────
const FeedbackForm: React.FC<FeedbackFormProps> = ({
  show,
  onClose,
  interviewId,
  candidateName,
  onSubmitted,
}) => {
  const [overallRating, setOverallRating] = useState(0);
  const [recommendation, setRecommendation] = useState<string | null>(null);
  const [detailedFeedback, setDetailedFeedback] = useState("");
  const [strengths, setStrengths] = useState("");
  const [weaknesses, setWeaknesses] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [interview, setInterview] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dynamicRatings, setDynamicRatings] = useState<Record<string, number>>(
    {}
  );

  const dispatch = useDispatch<AppDispatch>();

  // Static fallback criteria
  const staticCriteria: { key: string; label: string; icon: string; iconColor: string; iconBg: string }[] = [
    { key: "technical_skills", label: "Technical Skills", icon: "ti-code", iconColor: "#7c3aed", iconBg: "#f5f3ff" },
    { key: "communication", label: "Communication", icon: "ti-messages", iconColor: "#0369a1", iconBg: "#eff6ff" },
    { key: "problem_solving", label: "Problem Solving", icon: "ti-bulb", iconColor: "#d97706", iconBg: "#fffbeb" },
    { key: "cultural_fit", label: "Culture Fit", icon: "ti-users", iconColor: "#059669", iconBg: "#ecfdf5" },
  ];

  React.useEffect(() => {
    if (show && interviewId) {
      setLoading(true);
      dispatch(fetchInterviewDetails(interviewId))
        .unwrap()
        .then((data: any) => {
          setInterview(data);
          if (data.round?.criteria_schema) {
            const initial: Record<string, number> = {};
            data.round.criteria_schema.forEach((c: CriteriaSchemaItem) => {
              initial[c.key] = 0;
            });
            setDynamicRatings(initial);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [show, interviewId, dispatch]);

  const resetForm = () => {
    setOverallRating(0);
    setRecommendation(null);
    setDetailedFeedback("");
    setStrengths("");
    setWeaknesses("");
    setDynamicRatings({});
  };

  const handleSubmit = async () => {
    if (!recommendation) {
      notify.warn("Please select a recommendation.");
      return;
    }
    if (overallRating === 0) {
      notify.warn("Please provide an overall rating.");
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(
        submitInterviewFeedback({
          interview_id: interviewId,
          overall_rating: overallRating,
          recommendation,
          detailed_feedback: detailedFeedback,
          strengths: strengths || undefined,
          weaknesses: weaknesses || undefined,
          criteria_scores:
            Object.keys(dynamicRatings).length > 0
              ? Object.entries(dynamicRatings).map(([key, val]) => {
                const criterion =
                  interview?.round?.criteria_schema?.find(
                    (c: any) => c.key === key
                  );
                return {
                  criterion_key: key,
                  label: criterion?.label || key,
                  rating: val,
                };
              })
              : undefined,
        })
      ).unwrap();

      notify.success("Feedback submitted successfully");
      onSubmitted();
      onClose();
      resetForm();
    } catch (err: any) {
      notify.error(
        typeof err === "string" ? err : err?.message || "Failed to submit feedback"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Decide which criteria to render
  const hasDynamic =
    !loading && interview?.round?.criteria_schema?.length > 0;
  const criteriaToRender: {
    key: string;
    label: string;
    icon: string;
    iconColor: string;
    iconBg: string;
  }[] = hasDynamic
      ? interview.round.criteria_schema.map((c: CriteriaSchemaItem) => ({
        key: c.key,
        label: c.label,
        icon: "ti-circle-check",
        iconColor: "#7c3aed",
        iconBg: "#f5f3ff",
      }))
      : staticCriteria;

  const selectedRecMeta = recommendation ? REC_META[recommendation] : null;

  return (
    <Modal
      open={show}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <i className="ti ti-send" style={{ fontSize: 13 }} />
          Submit Feedback
        </span>
      }
      okButtonProps={{
        loading: submitting,
        style: {
          background: "#2563eb",
          borderColor: "#2563eb",
          borderRadius: 8,
          fontWeight: 600,
          height: 36,
          paddingInline: 20,
        },
      }}
      cancelButtonProps={{
        disabled: submitting,
        style: { borderRadius: 8, height: 36 },
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              flexShrink: 0,
              background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
              border: "1px solid #bfdbfe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="ti ti-message-star"
              style={{ fontSize: 18, color: "#2563eb" }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.25,
              }}
            >
              Interview Evaluation
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#94a3b8",
                fontWeight: 400,
                marginTop: 2,
              }}
            >
              Feedback for{" "}
              <span style={{ color: "#2563eb", fontWeight: 600 }}>
                {candidateName}
              </span>
            </div>
          </div>
        </div>
      }
      width={700}
      destroyOnClose
      zIndex={1111}
      styles={{
        mask: {
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(15, 23, 42, 0.4)",
        },
        body: { padding: "20px 0 4px" },
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ── Overall Rating ───────────────────────────────────────────────── */}
        <div
          style={{
            background:
              overallRating > 0
                ? "linear-gradient(135deg, #fffbeb 0%, #fef9ee 100%)"
                : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            border: `1.5px solid ${overallRating > 0 ? "#fcd34d" : "#e2e8f0"}`,
            borderRadius: 14,
            padding: "18px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            transition: "all 0.2s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: overallRating > 0 ? "#fef3c7" : "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.2s",
              }}
            >
              <i
                className="ti ti-star"
                style={{
                  fontSize: 20,
                  color: overallRating > 0 ? "#f59e0b" : "#cbd5e1",
                }}
              />
            </div>
            <div>
              <div
                style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}
              >
                Overall Rating{" "}
                <span style={{ color: "#ef4444" }}>*</span>
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                Your holistic impression of the candidate
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <Rate
              value={overallRating}
              onChange={setOverallRating}
              style={{ fontSize: 26, color: "#f59e0b" }}
            />
            {overallRating > 0 && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#92400e",
                  background: "#fef3c7",
                  padding: "2px 8px",
                  borderRadius: 20,
                }}
              >
                {["", "Poor", "Fair", "Good", "Great", "Excellent"][overallRating]}
              </span>
            )}
          </div>
        </div>

        {/* ── Dimension Ratings ────────────────────────────────────────────── */}
        <div>
          <SectionHeader
            icon="ti-layout-grid"
            iconColor="#7c3aed"
            iconBg="#f5f3ff"
            title="Dimension Ratings"
          />

          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "20px 0",
                color: "#94a3b8",
                fontSize: 13,
              }}
            >
              <div
                style={{
                  width: 16,
                  height: 16,
                  border: "2px solid #e2e8f0",
                  borderTopColor: "#7c3aed",
                  borderRadius: "50%",
                  animation: "spin 0.7s linear infinite",
                }}
              />
              Loading round criteria…
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 10,
              }}
            >
              {criteriaToRender.map((c) => (
                <RatingCard
                  key={c.key}
                  icon={c.icon}
                  iconColor={c.iconColor}
                  iconBg={c.iconBg}
                  label={c.label}
                  value={
                    hasDynamic
                      ? dynamicRatings[c.key] || 0
                      : dynamicRatings[c.key] || 0
                  }
                  onChange={(val) =>
                    setDynamicRatings((prev) => ({ ...prev, [c.key]: val }))
                  }
                />
              ))}
            </div>
          )}
        </div>

        <Divider style={{ margin: "0" }} />

        {/* ── Recommendation ───────────────────────────────────────────────── */}
        <div>
          <SectionHeader
            icon="ti-thumb-up"
            iconColor="#2563eb"
            iconBg="#eff6ff"
            title="Final Decision"
          />
          <Select
            style={{ width: "100%" }}
            placeholder="Select a recommendation…"
            value={recommendation}
            onChange={setRecommendation}
            size="large"
            options={RECOMMENDATION_OPTIONS.map((opt: any) => ({
              value: opt.value,
              label: opt.label,
            }))}
            optionRender={(opt) => {
              const meta = REC_META[opt.value as string];
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "4px 0",
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      background: meta?.bg || "#f8fafc",
                      border: `1px solid ${meta?.border || "#e2e8f0"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className={`ti ${meta?.icon || "ti-circle"}`}
                      style={{ fontSize: 13, color: meta?.color || "#64748b" }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {opt.label}
                  </span>
                </div>
              );
            }}
          />
          {/* Selected recommendation pill */}
          {selectedRecMeta && recommendation && (
            <div
              style={{
                marginTop: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 12px",
                borderRadius: 20,
                background: selectedRecMeta.bg,
                border: `1px solid ${selectedRecMeta.border}`,
                fontSize: 12,
                fontWeight: 600,
                color: selectedRecMeta.color,
              }}
            >
              <i
                className={`ti ${selectedRecMeta.icon}`}
                style={{ fontSize: 13 }}
              />
              {RECOMMENDATION_OPTIONS.find((o: any) => o.value === recommendation)
                ?.label}
            </div>
          )}
        </div>

        {/* ── Strengths & Weaknesses ───────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <Label text="Strengths" />
            <TextArea
              rows={3}
              placeholder="Key strengths observed…"
              value={strengths}
              onChange={(e) => setStrengths(e.target.value)}
              style={{ borderRadius: 10, resize: "none", fontSize: 13 }}
            />
          </div>
          <div>
            <Label text="Areas for Improvement" />
            <TextArea
              rows={3}
              placeholder="Areas needing improvement…"
              value={weaknesses}
              onChange={(e) => setWeaknesses(e.target.value)}
              style={{ borderRadius: 10, resize: "none", fontSize: 13 }}
            />
          </div>
        </div>

        {/* ── Detailed Notes ───────────────────────────────────────────────── */}
        <div>
          <SectionHeader
            icon="ti-align-left"
            iconColor="#0369a1"
            iconBg="#eff6ff"
            title="Detailed Notes"
          />
          <TextArea
            rows={4}
            placeholder="Provide detailed feedback about the candidate's performance…"
            value={detailedFeedback}
            onChange={(e) => setDetailedFeedback(e.target.value)}
            style={{ borderRadius: 10, resize: "none", fontSize: 13 }}
          />
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: "#94a3b8",
              textAlign: "right",
            }}
          >
            {detailedFeedback.length} characters
          </div>
        </div>
      </div>

      {/* Spin keyframe for loader */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Modal>
  );
};

export default FeedbackForm;